import type { EditableFields } from '@terra/ui-design-system';
import { normalizeFindingPatch } from '../../utils/findingEditableFieldKeys';
import { useFindingEditStore } from '../../store/findingEditStore';
import { useRoleEditStore } from '../../store/roleEditStore';
import { useCredentialEditStore } from '../../store/credentialEditStore';
import { useCredentialAuthMaterialEditStore } from '../../store/credentialAuthMaterialEditStore';
import { useApplicationCopilotEditContextStore } from '../../store/applicationCopilotEditContextStore';
import {
  CREDENTIAL_OBJECT_KIND,
  CUSTOM_ROLE_OBJECT_KIND,
  isChatEditableObjectKind,
} from '../../utils/chatEditableObjectUtils';

export type ObjectDraftHandler = {
  stagePatch: ({
    objectId,
    patch,
    reason,
    baseFields,
    sourceUpdateId,
  }: {
    objectId: string;
    patch: Record<string, unknown>;
    reason?: string;
    baseFields?: Record<string, unknown>;
    sourceUpdateId?: string;
  }) => boolean;
};

const findingDraftHandler: ObjectDraftHandler = {
  stagePatch: ({ objectId, patch, reason, baseFields, sourceUpdateId }) => {
    const store = useFindingEditStore.getState();
    const editState = store.getFindingEditState(objectId);

    useFindingEditStore.getState().stageAiPatch(objectId, {
      patch: normalizeFindingPatch(patch),
      reason,
      baseFields:
        (baseFields as EditableFields | undefined) ?? editState?.editableFields,
      sourceUpdateId,
    });

    return true;
  },
};

const CUSTOM_ROLE_EDITABLE_FIELD_KEYS = ['name', 'description'] as const;

function normalizeCustomRolePatch(patch: Record<string, unknown>): {
  name?: string;
  description?: string;
} {
  const entries = CUSTOM_ROLE_EDITABLE_FIELD_KEYS.flatMap((key) => {
    const value = patch[key];

    if (value === undefined) {
      return [];
    }

    return [[key, typeof value === 'string' ? value : String(value)] as const];
  });

  return Object.fromEntries(entries) as { name?: string; description?: string };
}

const customRoleDraftHandler: ObjectDraftHandler = {
  stagePatch: ({ objectId, patch, reason, baseFields, sourceUpdateId }) => {
    const draftFields = useRoleEditStore.getState().getDraftFields(objectId);
    const catalogBaseFields = useApplicationCopilotEditContextStore
      .getState()
      .getRoleBaseFields(objectId);
    const resolvedBaseFields = baseFields ?? catalogBaseFields ?? draftFields;
    const normalizedBaseFields = resolvedBaseFields
      ? {
          name:
            typeof resolvedBaseFields.name === 'string'
              ? resolvedBaseFields.name
              : '',
          description:
            typeof resolvedBaseFields.description === 'string'
              ? resolvedBaseFields.description
              : '',
        }
      : undefined;

    useRoleEditStore.getState().stageAiPatch(objectId, {
      patch: normalizeCustomRolePatch(patch),
      reason,
      baseFields: normalizedBaseFields,
      sourceUpdateId,
    });

    return true;
  },
};

const CREDENTIAL_ROLE_EDITABLE_FIELD_KEYS = [
  'role',
  'roleId',
  'roleDescription',
] as const;

const CREDENTIAL_AUTH_MATERIAL_EDITABLE_FIELD_KEYS = [
  'headers',
  'cookies',
] as const;

function normalizeCredentialRolePatch(patch: Record<string, unknown>): {
  role?: string;
  roleId?: string | null;
  roleDescription?: string | null;
} {
  const normalized: {
    role?: string;
    roleId?: string | null;
    roleDescription?: string | null;
  } = {};

  for (const key of CREDENTIAL_ROLE_EDITABLE_FIELD_KEYS) {
    const value = patch[key];

    if (value === undefined) {
      continue;
    }

    if (key === 'roleId' || key === 'roleDescription') {
      normalized[key] = value === null ? null : String(value);
      continue;
    }

    normalized.role = typeof value === 'string' ? value : String(value);
  }

  return normalized;
}

function normalizeCredentialAuthMaterialPatch(patch: Record<string, unknown>): {
  headers?: Array<{ key: string; value: string }>;
  cookies?: Array<{ name: string; value: string }>;
} {
  const normalized: {
    headers?: Array<{ key: string; value: string }>;
    cookies?: Array<{ name: string; value: string }>;
  } = {};

  const headers = patch.headers;

  if (Array.isArray(headers)) {
    const parsedHeaders = headers.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') {
        return [];
      }

      const key = (entry as { key?: unknown }).key;
      const value = (entry as { value?: unknown }).value;

      if (typeof key !== 'string' || typeof value !== 'string') {
        return [];
      }

      return [{ key, value }];
    });

    if (parsedHeaders.length > 0) {
      normalized.headers = parsedHeaders;
    }
  }

  const cookies = patch.cookies;

  if (Array.isArray(cookies)) {
    const parsedCookies = cookies.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') {
        return [];
      }

      const name = (entry as { name?: unknown }).name;
      const value = (entry as { value?: unknown }).value;

      if (typeof name !== 'string' || typeof value !== 'string') {
        return [];
      }

      return [{ name, value }];
    });

    if (parsedCookies.length > 0) {
      normalized.cookies = parsedCookies;
    }
  }

  return normalized;
}

function isCredentialAuthMaterialPatch(
  patch: Record<string, unknown>,
): boolean {
  return CREDENTIAL_AUTH_MATERIAL_EDITABLE_FIELD_KEYS.some(
    (field) => patch[field] !== undefined,
  );
}

function isCredentialRolePatch(patch: Record<string, unknown>): boolean {
  return CREDENTIAL_ROLE_EDITABLE_FIELD_KEYS.some(
    (field) => patch[field] !== undefined,
  );
}

const credentialRoleDraftHandler: ObjectDraftHandler = {
  stagePatch: ({ objectId, patch, reason, baseFields, sourceUpdateId }) => {
    const draftFields = useCredentialEditStore
      .getState()
      .getDraftFields(objectId);
    const catalogBaseFields = useApplicationCopilotEditContextStore
      .getState()
      .getCredentialBaseFields(objectId);
    const resolvedBaseFields = baseFields ?? catalogBaseFields ?? draftFields;
    const normalizedBaseFields = resolvedBaseFields
      ? {
          role:
            typeof resolvedBaseFields.role === 'string'
              ? resolvedBaseFields.role
              : '',
          roleId:
            resolvedBaseFields.roleId === null
              ? null
              : typeof resolvedBaseFields.roleId === 'string'
                ? resolvedBaseFields.roleId
                : null,
          roleDescription:
            resolvedBaseFields.roleDescription === null
              ? null
              : typeof resolvedBaseFields.roleDescription === 'string'
                ? resolvedBaseFields.roleDescription
                : null,
        }
      : undefined;

    return useCredentialEditStore.getState().stageAiPatch(objectId, {
      patch: normalizeCredentialRolePatch(patch),
      reason,
      baseFields: normalizedBaseFields,
      sourceUpdateId,
    });
  },
};

const credentialAuthMaterialDraftHandler: ObjectDraftHandler = {
  stagePatch: ({ objectId, patch, reason, baseFields, sourceUpdateId }) => {
    const normalizedPatch = normalizeCredentialAuthMaterialPatch(patch);

    if (Object.keys(normalizedPatch).length === 0) {
      return false;
    }

    const resolvedBaseFields =
      baseFields &&
      Array.isArray(baseFields.headers) &&
      Array.isArray(baseFields.cookies)
        ? {
            headers: baseFields.headers as Array<{
              key: string;
              value: string;
            }>,
            cookies: baseFields.cookies as Array<{
              name: string;
              value: string;
            }>,
          }
        : (() => {
            const catalogBaseFields = useApplicationCopilotEditContextStore
              .getState()
              .getCredentialBaseFields(objectId);

            if (!catalogBaseFields) {
              return undefined;
            }

            return {
              headers: catalogBaseFields.headers,
              cookies: catalogBaseFields.cookies,
            };
          })();

    return useCredentialAuthMaterialEditStore
      .getState()
      .stageAiPatch(objectId, {
        patch: normalizedPatch,
        reason,
        baseFields: resolvedBaseFields,
        sourceUpdateId,
      });
  },
};

const credentialDraftHandler: ObjectDraftHandler = {
  stagePatch: (args) => {
    let staged = false;

    if (isCredentialAuthMaterialPatch(args.patch)) {
      staged = credentialAuthMaterialDraftHandler.stagePatch(args) || staged;
    }

    if (isCredentialRolePatch(args.patch)) {
      staged = credentialRoleDraftHandler.stagePatch(args) || staged;
    }

    return staged;
  },
};

const OBJECT_DRAFT_HANDLERS: Record<string, ObjectDraftHandler> = {
  Finding: findingDraftHandler,
  [CUSTOM_ROLE_OBJECT_KIND]: customRoleDraftHandler,
  [CREDENTIAL_OBJECT_KIND]: credentialDraftHandler,
};

function getObjectDraftHandler(
  objectKind: string,
): ObjectDraftHandler | undefined {
  return OBJECT_DRAFT_HANDLERS[objectKind];
}

export function stageObjectUpdate({
  objectKind,
  objectId,
  patch,
  reason,
  baseFields,
  sourceUpdateId,
}: {
  objectKind: string;
  objectId: string;
  patch: Record<string, unknown>;
  reason?: string;
  baseFields?: Record<string, unknown>;
  sourceUpdateId?: string;
}): boolean {
  if (!isChatEditableObjectKind(objectKind)) {
    return false;
  }

  const handler = getObjectDraftHandler(objectKind);

  if (!handler) {
    return false;
  }

  return handler.stagePatch({
    objectId,
    patch,
    reason,
    baseFields,
    sourceUpdateId,
  });
}
